// What the read-only hazard details panel shows, computed from the GraphQL
// hazard(id:) result. Pure — the clock and the translator are parameters — so
// every string is unit-tested without mounting the panel.
//
// Copy and rules follow the iOS reference (HazardCopy / HazardDetailsContent).
// NEVER shows a lifespan, timer, expiry or any server rule: the product rules
// forbid it. The web is view-only, so there are no vote, report, flag or delete
// actions here either.

import { ConfirmationState, HazardType, Passability, confirmationStateFromWire, hazardTypeFromWire, passabilityFromWire } from './hazardTypes';

/**
 * vue-i18n's `t`, narrowed to what this needs: named values, or a count, which
 * picks a form of a pipe-separated plural message ("1 hiker | {count} hikers")
 * by the locale's plural rule (Irish has five — i18n/pluralRules.ts).
 */
export type Translate = (key: string, values?: Record<string, unknown> | number) => string;

export interface HazardPhoto {
  id: string;
  position: number;
  url: string;
  thumbnailUrl: string;
}

/** A hiker who confirmed the hazard, as the avatar stack draws them. */
export interface HazardConfirmer {
  /** Their profile photo; null when they have none or their profile is private. */
  avatarUrl: string | null;
  /** Up to two letters; null when there is no name (the stack then shows the HiiKER placeholder). */
  initials: string | null;
}

export interface Hazard {
  id: string;
  hazardType: HazardType;
  passability: Passability;
  confirmationState: ConfirmationState;
  status: string;
  /** The pin; null when the payload has none (a `?hazard=` link then opens the card without flying). */
  latitude: number | null;
  longitude: number | null;
  reportedAt: Date | null;
  lastConfirmedAt: Date | null;
  confirmationsCount: number;
  /** Up to 3 of those hikers, most recent first (never the reporter). */
  confirmedBy: HazardConfirmer[];
  photos: HazardPhoto[];
  isMine: boolean;
}

function finiteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function parseDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The GraphQL hazard as the panel's model. Null when the payload is missing or
 * carries a type/state the web doesn't know (a newer server than this bundle):
 * better no panel than a wrong one.
 */
export function normalizeHazard(raw: any): Hazard | null {
  if (!raw || typeof raw !== 'object' || raw.id === null || raw.id === undefined) return null;
  const hazardType = hazardTypeFromWire(raw.hazardType);
  const confirmationState = confirmationStateFromWire(raw.confirmationState);
  const passability = passabilityFromWire(raw.passability);
  if (!hazardType || !confirmationState || !passability) return null;

  const photos: HazardPhoto[] = (Array.isArray(raw.photos) ? raw.photos : [])
    .filter((photo: any) => photo && typeof photo.url === 'string' && typeof photo.thumbnailUrl === 'string')
    .map((photo: any) => ({
      id: String(photo.id),
      position: Number(photo.position) || 0,
      url: photo.url,
      thumbnailUrl: photo.thumbnailUrl,
    }))
    .sort((a: HazardPhoto, b: HazardPhoto) => a.position - b.position);

  return {
    id: String(raw.id),
    hazardType,
    passability,
    confirmationState,
    status: typeof raw.status === 'string' ? raw.status.toUpperCase() : '',
    latitude: finiteOrNull(raw.latitude),
    longitude: finiteOrNull(raw.longitude),
    reportedAt: parseDate(raw.reportedAt),
    lastConfirmedAt: parseDate(raw.lastConfirmedAt),
    confirmationsCount: Math.max(0, Number(raw.confirmationsCount) || 0),
    confirmedBy: (Array.isArray(raw.confirmedBy) ? raw.confirmedBy : [])
      .filter((hiker: any) => hiker && typeof hiker === 'object')
      .map((hiker: any) => ({
        avatarUrl: typeof hiker.avatar?.smUrl === 'string' && hiker.avatar.smUrl ? hiker.avatar.smUrl : null,
        initials: typeof hiker.initials === 'string' && hiker.initials.trim() ? hiker.initials.trim() : null,
      })),
    photos,
    isMine: raw.isMine === true,
  };
}

/** Only ACTIVE hazards are on the map; anything else has "already left the map". */
export function isHazardOnMap(hazard: Hazard | null | undefined): hazard is Hazard {
  return !!hazard && hazard.status === 'ACTIVE';
}

/** The type's display name. Literal keys, so the i18n client bundle ships each one. */
export function hazardTypeName(type: HazardType, t: Translate): string {
  switch (type) {
    case 'flooded_path': return t('trail_hazards.types.flooded_path');
    case 'tree_down': return t('trail_hazards.types.tree_down');
    case 'path_closed': return t('trail_hazards.types.path_closed');
    case 'livestock_wildlife': return t('trail_hazards.types.livestock_wildlife');
    case 'path_hard_to_follow': return t('trail_hazards.types.path_hard_to_follow');
    case 'boggy_ground': return t('trail_hazards.types.boggy_ground');
    case 'heavy_vegetation': return t('trail_hazards.types.heavy_vegetation');
    case 'rockfall_landslip': return t('trail_hazards.types.rockfall_landslip');
    case 'ice_snow': return t('trail_hazards.types.ice_snow');
    case 'bridge_damage': return t('trail_hazards.types.bridge_damage');
    case 'fire': return t('trail_hazards.types.fire');
    default: return '';
  }
}

/**
 * "just now", "35 min ago", "3 h ago", "1 day ago", "2 days ago". A future
 * date (clock skew) reads as "just now".
 */
export function relativeTime(date: Date, now: Date, t: Translate): string {
  const seconds = (now.getTime() - date.getTime()) / 1000;
  if (!(seconds >= 60)) return t('trail_hazards.time.just_now');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('trail_hazards.time.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('trail_hazards.time.hours', { count: hours });
  const days = Math.floor(hours / 24);
  if (days === 1) return t('trail_hazards.time.day');
  return t('trail_hazards.time.days', { count: days });
}

/** "1 hiker" / "4 hikers"; Irish "2 shiúlóir", "7 siúlóir". */
export function hikersText(count: number, t: Translate): string {
  return t('trail_hazards.hikers', count);
}

/** "1 photo" / "3 photos"; Irish "3 ghrianghraf", "7 ngrianghraf", "11 grianghraf". */
export function photoCountText(count: number, t: Translate): string {
  return t('trail_hazards.photo_count', count);
}

export type TagKind = 'unconfirmed' | 'confirmed' | 'passable' | 'blocked';

export interface HazardTag {
  kind: TagKind;
  text: string;
}

export function stateTag(state: ConfirmationState, t: Translate): HazardTag {
  return state === 'confirmed'
    ? { kind: 'confirmed', text: t('trail_hazards.state.confirmed') }
    : { kind: 'unconfirmed', text: t('trail_hazards.state.unconfirmed') };
}

/** "Passable" (orange) / "Blocked" (red). */
export function passabilityTag(passability: Passability, t: Translate): HazardTag {
  return passability === 'blocked'
    ? { kind: 'blocked', text: t('trail_hazards.passability.blocked_tag') }
    : { kind: 'passable', text: t('trail_hazards.passability.passable_tag') };
}

/**
 * Confirmed only: the parts of "Confirmed by **4 hikers** · 35
 * min ago". `time` is null when the server has no last-confirmed time, which
 * reads "Confirmed by 4 hikers". Null for an unconfirmed hazard.
 */
/**
 * One circle of the "Confirmed by" avatar stack: a hiker (photo, else
 * initials, else the HiiKER placeholder) or the +N counter in the last slot.
 */
export type ConfirmerSlot =
  | { kind: 'hiker'; avatarUrl: string | null; initials: string | null }
  | { kind: 'counter'; label: string };

/** The stack shows this many circles at most. */
export const CONFIRMER_SLOTS = 3;
/** Past this many more, the counter reads "99+". */
export const CONFIRMER_COUNTER_MAX = 99;

/**
 * The avatar stack for `count` hikers, most recent first. Up to three hikers:
 * one circle each. More: the two most recent, then "+N" for the rest ("99+"
 * past 99). A hiker the server didn't list (a deleted account) still gets a
 * circle, so the stack always matches the count. Same rule on iOS and Android.
 */
export function confirmerSlots(count: number, confirmers: HazardConfirmer[]): ConfirmerSlot[] {
  const total = Math.max(0, Math.floor(count));
  const hikers = total > CONFIRMER_SLOTS ? CONFIRMER_SLOTS - 1 : total;
  const slots: ConfirmerSlot[] = Array.from({ length: hikers }, (_, index) => {
    const hiker = confirmers[index];
    return { kind: 'hiker', avatarUrl: hiker?.avatarUrl ?? null, initials: hiker?.initials ?? null };
  });
  if (total > CONFIRMER_SLOTS) {
    const rest = total - hikers;
    slots.push({ kind: 'counter', label: rest > CONFIRMER_COUNTER_MAX ? `${CONFIRMER_COUNTER_MAX}+` : `+${rest}` });
  }
  return slots;
}

export interface ConfirmationSummary {
  hikers: string;
  time: string | null;
  /** The avatar stack beside the text. */
  avatars: ConfirmerSlot[];
}

/**
 * The state tag the panel shows, or none. A confirmed hazard's "Confirmed by N
 * hikers" card already says it, so the green "Confirmed" tag only shows when
 * that card doesn't (it always does today). An unconfirmed hazard always shows
 * its "Unconfirmed" tag (there is no card for it). Same rule on iOS and Android.
 */
export function visibleStateTag(
  state: ConfirmationState,
  confirmation: ConfirmationSummary | null,
  t: Translate,
): HazardTag | null {
  if (state === 'confirmed' && confirmation) return null;
  return stateTag(state, t);
}

export function confirmationSummary(hazard: Hazard, now: Date, t: Translate): ConfirmationSummary | null {
  if (hazard.confirmationState !== 'confirmed') return null;
  const count = Math.max(1, hazard.confirmationsCount);
  return {
    hikers: hikersText(count, t),
    time: hazard.lastConfirmedAt ? relativeTime(hazard.lastConfirmedAt, now, t) : null,
    avatars: confirmerSlots(count, hazard.confirmedBy),
  };
}

export interface HazardDetailsPhoto {
  key: string;
  thumbUrl: string;
  fullUrl: string;
}

export interface HazardDetailsView {
  id: string;
  hazardType: HazardType;
  /** Current passability: picks the icon (red triangle blocked, orange passable). */
  passability: Passability;
  title: string;
  /**
   * "Reported by you 3 h ago" on the viewer's own report; null otherwise (the
   * title and icon already say it's a hazard, and the tags say when).
   */
  subtitle: string | null;
  isMine: boolean;
  /** Null while the "Confirmed by" card shows ({@link visibleStateTag}). */
  stateTag: HazardTag | null;
  passabilityTag: HazardTag;
  /** "Reported 3 h ago"; null on the viewer's own report, whose subtitle already says it. */
  reportedAgo: string | null;
  confirmation: ConfirmationSummary | null;
  photos: HazardDetailsPhoto[];
  photoCount: string;
}

export function buildHazardDetails(hazard: Hazard, now: Date, t: Translate): HazardDetailsView {
  const ago = hazard.reportedAt ? relativeTime(hazard.reportedAt, now, t) : null;
  const subtitle = hazard.isMine && ago ? t('trail_hazards.reported_by_you', { time: ago }) : null;
  const confirmation = confirmationSummary(hazard, now, t);

  return {
    id: hazard.id,
    hazardType: hazard.hazardType,
    passability: hazard.passability,
    title: hazardTypeName(hazard.hazardType, t),
    subtitle,
    isMine: hazard.isMine,
    stateTag: visibleStateTag(hazard.confirmationState, confirmation, t),
    passabilityTag: passabilityTag(hazard.passability, t),
    reportedAgo: hazard.isMine || !ago ? null : t('trail_hazards.reported_ago', { time: ago }),
    confirmation,
    photos: hazard.photos.map((photo) => ({ key: photo.id, thumbUrl: photo.thumbnailUrl, fullUrl: photo.url })),
    photoCount: photoCountText(hazard.photos.length, t),
  };
}
